import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaPlus, FaEye, FaEdit } from 'react-icons/fa';
import './DefendantList.css';

const DefendantList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [defendants, setDefendants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showIncomplete, setShowIncomplete] = useState(searchParams.get('incomplete') === 'true');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    // Check for incomplete query param on mount
    if (searchParams.get('incomplete') === 'true') {
      setShowIncomplete(true);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchDefendants();
  }, [pagination.page, searchTerm, showIncomplete]);

  const fetchDefendants = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(showIncomplete && { incomplete: 'true' })
      };

      const response = await axios.get('/defendants', { params });

      if (response.data.success) {
        setDefendants(response.data.data);
        setPagination({ ...pagination, ...response.data.pagination });
      }
    } catch (error) {
      console.error('Failed to fetch defendants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Defendants</h1>
          <p className="page-subtitle">Manage all defendant records</p>
        </div>
        <button className="btn btn-primary">
          <FaPlus /> New Defendant
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, phone, or SSN..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading defendants...</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>DOB</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Active Bonds</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {defendants.length > 0 ? (
                  defendants.map((defendant) => (
                    <tr key={defendant.id}>
                      <td>
                        <strong
                          style={{
                            cursor: 'pointer',
                            color: '#d4a017',
                            textDecoration: 'none'
                          }}
                          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          onClick={() => navigate(`/defendants/${defendant.id}`)}
                        >
                          {defendant.first_name} {defendant.last_name}
                        </strong>
                      </td>
                      <td>{defendant.email || '-'}</td>
                      <td>{new Date(defendant.date_of_birth).toLocaleDateString()}</td>
                      <td>{defendant.phone_primary || '-'}</td>
                      <td>
                        {defendant.address_city ? `${defendant.address_city}, ${defendant.address_state}` : '-'}
                      </td>
                      <td>{defendant.active_bond_count || 0}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            title="View Details"
                            onClick={() => navigate(`/defendants/${defendant.id}`)}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            title="Edit"
                            onClick={() => navigate(`/defendants/${defendant.id}`)}
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No defendants found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DefendantList;
